/**
 * Friendly error message registry.
 *
 * The backend tags failures with a stable `CODE`. There are two
 * possible carriers because Keystone *wraps* errors thrown inside
 * list hooks (`validateInput`, `beforeOperation`, …) and Apollo only
 * serializes the outer `extensions`, so a `GraphQLError({ extensions:
 * { code } })` thrown from a hook arrives with `extensions.code ===
 * "KS_VALIDATION_FAILURE_ERROR"` and the original code lost.
 *
 * To survive that, we use a tiny convention: **the backend prefixes
 * the message with `[CODE]`** (also setting `extensions.code` for the
 * rare case of a direct-resolver throw). The helper below checks both
 * carriers in order.
 *
 * Convention for codes: `{ENTITY}_{FIELD}_{REASON}` in SCREAMING_SNAKE_CASE,
 * e.g. `USER_EMAIL_TAKEN`, `COMPANY_NAME_TAKEN`. Keep them stable —
 * other surfaces (translations, analytics) will key off them.
 */

export const ERROR_MESSAGES = {
  // ── User ───────────────────────────────────────────────────────────────────
  USER_EMAIL_TAKEN: 'A user with this email already exists.',

  // ── Case ───────────────────────────────────────────────────────────────────
  CASE_LOST_STAGE_NOT_FOUND: 'No "Lost" stage is configured. Ask a SysAdmin to add one.',
  CASE_MARK_LOST_FORBIDDEN: 'You don\'t have permission to mark a case as Lost.',
  CASE_REACTIVATE_FORBIDDEN: 'You don\'t have permission to reactivate a case.',
  CASE_NO_ACTIVE_STAGE: 'No active stage is configured to restore the case to.',
  CASE_NOT_FOUND: 'The case no longer exists.',
  CASE_STAFF_NOT_ACTIVE: 'Only an Active staff member can be assigned to a case.',
}

/**
 * Whether the error carries one of the codes above (either path) — for a
 * caller that otherwise swallows errors but must surface a known refusal.
 *
 * @param {unknown} err
 * @returns {boolean}
 */
export function hasKnownErrorCode(err) {
  const gqlErr = err?.graphQLErrors?.[0]
  const code = gqlErr?.extensions?.code
  if (code && ERROR_MESSAGES[code]) return true
  const inline = (gqlErr?.message ?? err?.message ?? '').match(INLINE_CODE_RE)?.[1]

  return Boolean(inline && ERROR_MESSAGES[inline])
}

/** Matches the leading `[CODE]` token the backend embeds in hook errors. */
const INLINE_CODE_RE = /\[([A-Z][A-Z0-9_]*)\]/

/**
 * Picks the cleanest message we can produce from an Apollo / Keystone
 * error. Resolution order:
 *
 *   1. `extensions.code` from a direct-resolver throw (custom mutation).
 *   2. `[CODE]` embedded in the wrapped hook error's message.
 *   3. Raw Prisma P2002 (unique constraint) → generic "value in use"
 *      sentence. Covers gaps where the backend hasn't been wired with
 *      a soft validateInput check yet.
 *   4. The original server message (final usable fallback).
 *   5. Generic catch-all.
 *
 * @param {unknown} err  The error caught from an Apollo mutation/query.
 * @returns {string}     A short, user-facing message.
 */
export function getFriendlyErrorMessage(err) {
  const gqlErr = err?.graphQLErrors?.[0]
  const rawMessage = gqlErr?.message ?? err?.message ?? ''

  // 1. Direct-resolver path: extensions preserved by Apollo.
  const extCode = gqlErr?.extensions?.code
  if (extCode && ERROR_MESSAGES[extCode]) return ERROR_MESSAGES[extCode]

  // 2. Hook path: code survives inside the message text.
  const inlineCode = rawMessage.match(INLINE_CODE_RE)?.[1]
  if (inlineCode && ERROR_MESSAGES[inlineCode]) return ERROR_MESSAGES[inlineCode]

  // 3. Catch-all for Prisma's unique-constraint error when no specific
  //    code was thrown. The Prisma error code is P2002 and the message
  //    always contains "Unique constraint failed on".
  if (/P2002|Unique constraint failed/i.test(rawMessage)) {
    return 'This value is already in use.'
  }

  // 4 + 5. Server message, or a generic fallback. Strip the `[CODE]`
  //        prefix if it's there (unknown code, but no point exposing
  //        the marker to the user).
  return rawMessage.replace(INLINE_CODE_RE, '').trim() || 'An unexpected error occurred.'
}
